// bounty
import {append, create} from '@acng/frontend-bounty/dom/template.js';
import {body} from '@acng/frontend-bounty/dom/document.js';
import {remove as removeNode} from '@acng/frontend-bounty/dom/move.js';
// stargazer, rubicon & relativity
import {Watch, open} from '@acng/frontend-relativity';
import {AttributeLink, AttributeSetup, task} from '@acng/frontend-stargazer';
import {Engine, disconnect} from '@acng/frontend-stargazer';
import {IS, guard} from '@acng/frontend-rubicon';
// enterprise
import {ctxVipPoints} from '../context/points.js';
import {userHas} from '../service/cache.js';
import {HIDDEN, unset} from '@acng/frontend-bounty/dom/attribute.js';
import {add, createSet, has} from '@acng/frontend-bounty/collection.js';
import {authUser} from 'acng/userPool/context/auth-user.js';
import {debug} from '@acng/frontend-bounty/dom/debug.js';
import {attributeSelector} from '@acng/frontend-bounty/dom/query.js';
import {undefined} from '@acng/frontend-bounty/std/value.js';
import {compile} from '../service/template.js';

const DEBUG_ALL = false;

/**
 * Remove the element until the user has achieved a certain challenge.
 *
 * @example
 *
 * ```html
 * <div with-achievement="clicked-the-stuff">
 *   <strong>YES!</strong>
 *   You clicked the balloons and the bats.
 * </div>
 * ```
 *
 * @group DOM Attribute
 */
export const WITH_ACHIEVEMENT = 'with-achievement';

AttributeSetup(WITH_ACHIEVEMENT, (name) => {
  /**
   * Internal synchronous(!) cache for achieved challenges.
   *
   * @type {Set<string>}
   */
  const achievements = createSet();

  /**
   * @param {string} challengeName
   */
  const check = async (challengeName) => {
    if (await userHas(challengeName)) {
      add(achievements, challengeName);
    }
  };

  /**
   * There is no achievement event a.t.m.
   * So watch for points value.
   */
  const watch = Watch(attributeSelector(name), ctxVipPoints);

  AttributeLink(name, (element, achievement, linker) => {
    if (has(achievements, achievement)) {
      return;
    }

    const template = create();
    const dummy = element.cloneNode();
    const engine = new Engine(template);
    ASSERT: guard(dummy, IS(HTMLElement), `[${name}]`);
    DEBUG: if (debug(element))
      console.log(`stash [${name}=${achievement}]`, {element, dummy, linker, engine});

    dummy[HIDDEN] = true;
    element.replaceWith(dummy);
    open(body, template.content);
    unset(element, name);
    append(template, element);

    watch(dummy, async () => {
      if (await userHas(achievement)) {
        DEBUG: if (debug(element))
          console.log(`pop [${name}=${achievement}]`, {element, dummy, linker, engine});
        add(achievements, achievement);
        const fragment = engine.toFragment(undefined, undefined, dummy);
        dummy.replaceWith(fragment);
        engine.connect();
      }
    });

    return () => () => disconnect(engine);
  });

  return (element, achievement, engine) => {
    compile(element);

    DEBUG: if (DEBUG_ALL) element.setAttribute('debug', '');
    if (!authUser) {
      DEBUG: if (debug(element)) console.log('remove [${name}]', {});
      removeNode(element);

      return;
    }

    task(engine, check(achievement));
  };
});
